import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectProps,
  ListSubheader,
} from '@mui/material';
import { FieldProps, useFormikContext } from 'formik';
import { FC, useCallback } from 'react';
import styled from 'styled-components';

type Option = { id: string; label: string; disabled?: boolean };

export type MultiSelectProps = {
  options: Option[];
  validateBeforeChange?: (value: string[]) => boolean;
} & SelectProps &
  FieldProps<string[]>;

export const MultiSelect: FC<MultiSelectProps> = ({
  field,
  label,
  className,
  options,
  validateBeforeChange = () => true,
  ...restProps
}) => {
  const { submitForm } = useFormikContext();

  const value = field.value || [];

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value as string[];

      if (validateBeforeChange(value)) {
        field.onChange(event);
        submitForm();
      }
    },
    [field, submitForm, validateBeforeChange]
  );

  const getLabel = (id: string) => options.find((option) => option.id === id)?.label || '';

  const renderValue = (selected: unknown) => {
    return (
      <ChipsWrapper>
        {(selected as string[]).map((value) => (
          <Chip key={value} label={getLabel(value)} size="small" />
        ))}
      </ChipsWrapper>
    );
  };

  return (
    <FormControl className={className}>
      <InputLabel id={field.name}>{label}</InputLabel>

      <Select
        labelId={field.name}
        multiple
        input={<OutlinedInput label={label} />}
        size="small"
        renderValue={renderValue}
        {...field}
        {...restProps}
        onChange={handleChange}
        value={value}
      >
        {options.map(({ id, label, disabled }) =>
          id === 'subheader' ? (
            <ListSubheader key={id}>{label}</ListSubheader>
          ) : (
            <MenuItem key={id} value={id} disabled={disabled}>
              <Checkbox checked={Boolean(~value?.indexOf(id))} />
              <ListItemText primary={label} />
            </MenuItem>
          )
        )}
      </Select>
    </FormControl>
  );
};

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;
